import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, X } from "lucide-react";
import { FadeUp, MaskedLine, Eyebrow, EASE_OUT } from "@/components/Reveal";

const TRADITIONAL_FLOW = ["JD", "Keywords", "Resume", "Shortlist"];
const ROLESENSE_FLOW = ["Business Context", "Role Context", "Candidate Context", "Fitment", "Decision"];

const FACTORS = [
  "Role relevance", "Functional experience", "Technical fit", "Industry experience",
  "Career progression", "Leadership exposure", "Domain expertise", "Skill depth",
  "Experience gaps", "Role-specific strengths", "Location considerations", "Compensation context",
];

const CAPABILITIES = [
  { num: "01", title: "Contextual Resume Evaluation", desc: "Go beyond keyword matching. RoleSense evaluates a candidate against the actual requirements and context of a position — Key Strengths, Key Gaps, Relevant Experience, Fitment and Risk Indicators." },
  { num: "02", title: "JD Intelligence Builder", desc: "Transform a basic job requirement into a structured hiring mandate — core competencies, technical requirements, experience, seniority and critical skills." },
  { num: "03", title: "Technical Fitment Intelligence™", desc: "For technology roles, RoleSense evaluates technical relevance across skills, technologies and experience — where keyword matching produces misleading results." },
  { num: "04", title: "Profile Intelligence", desc: "A broader candidate view across multiple indicators rather than a single matching score — understand profile quality before investing time." },
  { num: "05", title: "Intelligent Resume Routing", desc: "Automatically organize and route relevant profiles against appropriate requirements — reducing manual screening and improving response time." },
  { num: "06", title: "Candidate Intelligence", desc: "Instead of asking 'Does this resume match?', RoleSense helps answer 'Why should we consider this candidate for this specific role?'" },
];

const SAMPLE_ROLES = [
  {
    id: "cto-saas",
    label: "CTO — B2B SaaS",
    candidate: "Candidate Profile · 14 yrs · SaaS & Cloud Infrastructure",
    scores: [["Role Relevance", 92], ["Technical Fit", 88], ["Industry Experience", 94], ["Leadership Exposure", 90], ["Career Progression", 86]],
    strengths: ["Scaled engineering org 40 → 180", "Two successful SaaS exits", "Deep cloud-native architecture"],
    gaps: ["Limited EMEA market exposure"],
    verdict: "Strong contextual fit — recommend partner interview",
  },
  {
    id: "insuretech-head",
    label: "Head of Insurance Technology",
    candidate: "Candidate Profile · 12 yrs · P&C Insurance Platforms",
    scores: [["Role Relevance", 89], ["Technical Fit", 84], ["Industry Experience", 95], ["Leadership Exposure", 82], ["Career Progression", 88]],
    strengths: ["Guidewire & Duck Creek implementations", "Core platform modernization", "Regulatory domain depth"],
    gaps: ["No direct P&L ownership"],
    verdict: "High domain fit — probe commercial leadership depth",
  },
  {
    id: "vp-healthtech",
    label: "VP Engineering — HealthTech",
    candidate: "Candidate Profile · 11 yrs · Healthcare SaaS & Digital Health",
    scores: [["Role Relevance", 87], ["Technical Fit", 91], ["Industry Experience", 85], ["Leadership Exposure", 83], ["Career Progression", 90]],
    strengths: ["HIPAA-compliant platform builds", "Distributed team leadership", "Data interoperability expertise"],
    gaps: ["Early-stage scaling experience limited"],
    verdict: "Strong technical fit — validate scale-up readiness",
  },
];

const TECH_HANDLES = ["Extraction", "Structuring", "Comparison", "Pattern Recognition", "Intelligence"];
const HUMAN_HANDLES = ["Judgment", "Relationship", "Motivation", "Validation", "Decision"];

const RoleSense = () => {
  const [roleId, setRoleId] = useState(SAMPLE_ROLES[0].id);
  const role = SAMPLE_ROLES.find((r) => r.id === roleId);

  return (
    <div data-testid="rolesense-page">
      {/* HERO */}
      <section className="bg-ink pt-36 text-paper lg:pt-44" data-testid="rolesense-hero">
        <div className="mx-auto max-w-7xl px-6 pb-20 lg:px-10 lg:pb-28">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <Eyebrow className="text-gold">Proprietary Technology Advantage</Eyebrow>
          </motion.div>
          <h1 className="mt-6 max-w-4xl font-serif text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>Meet RoleSense™</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="italic text-gold">Recruitment intelligence,</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>built for better decisions.</MaskedLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE_OUT }}
            className="mt-10 grid gap-10 lg:grid-cols-2"
          >
            <p className="max-w-xl text-base leading-relaxed text-paper/60 sm:text-lg">
              RoleSense™ is Ally eXecutive HR's proprietary recruitment
              intelligence platform — designed to bring greater context,
              consistency and intelligence into the recruitment process.
            </p>
            <p className="border-l-2 border-gold pl-6 font-serif text-xl italic leading-relaxed text-paper sm:text-2xl" data-testid="rolesense-motto">
              It doesn't replace recruiters.<br />It makes recruiters better.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CONTEXT VS KEYWORDS */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36" data-testid="context-comparison-section">
        <FadeUp>
          <Eyebrow>Why RoleSense™</Eyebrow>
          <h2 className="mt-5 max-w-3xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Traditional recruitment looks at the resume. RoleSense looks at the context.
          </h2>
        </FadeUp>
        <div className="mt-14 grid gap-px border border-line bg-line lg:grid-cols-2">
          <FadeUp className="bg-paper p-9 lg:p-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-faded">Conventional Process</p>
            <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="traditional-flow">
              {TRADITIONAL_FLOW.map((step, i) => (
                <span key={step} className="flex items-center gap-3">
                  <span className="border border-line-medium bg-paper-deep px-4 py-2.5 text-sm text-ink-faded line-through decoration-oxblood/50">{step}</span>
                  {i < TRADITIONAL_FLOW.length - 1 && <span className="text-ink-faded">→</span>}
                </span>
              ))}
            </div>
            <p className="mt-8 text-sm leading-relaxed text-ink-muted">
              Heavy reliance on keyword matching produces volume — not relevance.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="bg-ink p-9 text-paper lg:p-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">The RoleSense™ Layer</p>
            <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="rolesense-flow">
              {ROLESENSE_FLOW.map((step, i) => (
                <span key={step} className="flex items-center gap-3">
                  <span className={`border px-4 py-2.5 text-sm ${i === ROLESENSE_FLOW.length - 1 ? "border-gold bg-gold font-semibold text-ink" : "border-paper/25 text-paper/80"}`}>{step}</span>
                  {i < ROLESENSE_FLOW.length - 1 && <span className="text-gold">→</span>}
                </span>
              ))}
            </div>
            <p className="mt-8 text-sm leading-relaxed text-paper/60">
              A deeper layer of intelligence produces a more meaningful view of candidate suitability.
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.1} className="mt-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-faded">RoleSense evaluates</p>
          <div className="mt-5 flex flex-wrap gap-2.5" data-testid="evaluation-factors-list">
            {FACTORS.map((f) => (
              <span key={f} className="border border-line bg-paper-card px-4 py-2 text-xs font-medium text-ink-muted transition-colors duration-300 hover:border-gold hover:text-ink">{f}</span>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* CAPABILITIES */}
      <section className="border-y border-line bg-paper-deep py-24 lg:py-36" data-testid="capabilities-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Eyebrow>Capabilities</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Intelligence at every stage of the mandate.
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-px border border-line-medium bg-line-medium md:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => (
              <FadeUp key={c.num} delay={0.05 * i} className="group bg-paper p-8 transition-colors duration-500 hover:bg-paper-deep">
                <p className="font-serif text-4xl text-line-medium transition-colors duration-500 group-hover:text-gold" aria-hidden>{c.num}</p>
                <h3 className="mt-5 font-serif text-xl text-ink">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted">{c.desc}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FITMENT SIMULATOR */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36" data-testid="fitment-simulator-section">
        <FadeUp>
          <Eyebrow>A Sample of the Output</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Candidate intelligence, contextualized.
          </h2>
          <p className="mt-4 max-w-xl text-sm text-ink-muted">
            Select a mandate to see an illustrative RoleSense™ evaluation.
          </p>
        </FadeUp>
        <div className="mt-10 flex flex-wrap gap-3" role="tablist" aria-label="Sample mandates">
          {SAMPLE_ROLES.map((r) => (
            <button
              key={r.id}
              role="tab"
              aria-selected={roleId === r.id}
              onClick={() => setRoleId(r.id)}
              data-testid={`simulator-role-tab-${r.id}`}
              className={`border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                roleId === r.id ? "border-ink bg-ink text-paper" : "border-line-medium bg-paper-card text-ink-muted hover:border-ink hover:text-ink"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={role.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: EASE_OUT }}
            className="mt-8 grid gap-px border border-line-medium bg-line-medium lg:grid-cols-5"
            data-testid="simulator-result-card"
          >
            <div className="bg-ink p-9 text-paper lg:col-span-3 lg:p-12">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">RoleSense™ Evaluation</p>
              <h3 className="mt-4 font-serif text-2xl">{role.label}</h3>
              <p className="mt-1 text-sm text-paper/50">{role.candidate}</p>
              <div className="mt-9 space-y-5">
                {role.scores.map(([label, value], i) => (
                  <div key={label}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="text-paper/70">{label}</span>
                      <span className="font-mono text-gold">{value}</span>
                    </div>
                    <div className="mt-2 h-px bg-paper/15">
                      <motion.div
                        className="h-px bg-gold"
                        initial={{ width: 0 }}
                        animate={{ width: `${value}%` }}
                        transition={{ duration: 1, delay: 0.1 * i, ease: EASE_OUT }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-paper p-9 lg:col-span-2 lg:p-12">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Key Strengths</p>
                <ul className="mt-4 space-y-2.5">
                  {role.strengths.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 text-sm text-ink-muted">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" />{s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Key Gaps</p>
                <ul className="mt-4 space-y-2.5">
                  {role.gaps.map((g) => (
                    <li key={g} className="flex items-start gap-2.5 text-sm text-ink-muted">
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-oxblood" />{g}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="mt-8 border-t border-line pt-5 font-serif text-base italic text-ink" data-testid="simulator-verdict">
                "{role.verdict}"
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      {/* HUMAN + AI */}
      <section className="bg-ink py-24 text-paper lg:py-36" data-testid="human-ai-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Eyebrow className="text-gold">Human + AI</Eyebrow>
            <h2 className="mt-5 font-serif text-2xl tracking-tight sm:text-3xl lg:text-4xl">
              70% Human. 30% AI.
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-paper/60">
              Recruitment should never become an algorithmic exercise. AI can
              identify patterns, structure information and accelerate analysis —
              but human recruiters understand people, motivations, leadership
              potential and context.
            </p>
          </FadeUp>
          <div className="mt-12" data-testid="human-ai-bar">
            <div className="flex h-16 w-full overflow-hidden border border-paper/20">
              <motion.div
                className="flex items-center justify-center bg-gold px-4"
                initial={{ width: 0 }}
                whileInView={{ width: "70%" }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: EASE_OUT }}
              >
                <span className="whitespace-nowrap font-mono text-xs font-semibold uppercase tracking-[0.2em] text-ink">70% Human</span>
              </motion.div>
              <motion.div
                className="flex items-center justify-center bg-paper/10 px-4"
                initial={{ width: 0 }}
                whileInView={{ width: "30%" }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, delay: 0.15, ease: EASE_OUT }}
              >
                <span className="whitespace-nowrap font-mono text-xs uppercase tracking-[0.2em] text-paper/70">30% AI</span>
              </motion.div>
            </div>
          </div>
          <div className="mt-12 grid gap-px border border-paper/15 bg-paper/15 lg:grid-cols-2">
            <FadeUp className="bg-ink p-9 lg:p-12">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">Technology handles</p>
              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                {TECH_HANDLES.map((t, i) => (
                  <span key={t} className="flex items-center gap-2.5">
                    <span className="border border-paper/25 px-4 py-2 text-sm text-paper/80">{t}</span>
                    {i < TECH_HANDLES.length - 1 && <span className="text-gold">→</span>}
                  </span>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={0.1} className="bg-ink p-9 lg:p-12">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">Humans handle</p>
              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                {HUMAN_HANDLES.map((t, i) => (
                  <span key={t} className="flex items-center gap-2.5">
                    <span className="border border-gold bg-gold/10 px-4 py-2 text-sm font-medium text-gold">{t}</span>
                    {i < HUMAN_HANDLES.length - 1 && <span className="text-gold">→</span>}
                  </span>
                ))}
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="mt-12 text-center">
            <p className="font-serif text-xl italic text-paper/80 sm:text-2xl">
              Technology-assisted recruitment with human accountability.
            </p>
            <Link
              to="/about-contact#contact"
              className="group mt-9 inline-flex items-center gap-2 border border-gold px-8 py-4 text-sm font-semibold text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
              data-testid="rolesense-cta-button"
            >
              Experience RoleSense™ on Your Next Mandate
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default RoleSense;
